export type UiHapticType =
  | 'selection'
  | 'impactLight'
  | 'impactMedium'
  | 'impactHeavy'
  | 'success'
  | 'warning'
  | 'error'
  | 'notificationSuccess'
  | 'notificationWarning'
  | 'notificationError'
  | 'none';

/**
 * Trigger subtle tap feedback (10ms tick) for selections, avatar toggles, tabs, chips
 */
export const triggerSelectionHaptic = (): boolean => {
  if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
    try {
      return navigator.vibrate(10);
    } catch {
      return false;
    }
  }
  return false;
};

/**
 * Trigger physical impact feedback for button presses, CTA actions, save operations
 */
export const triggerImpactHaptic = (
  style: 'light' | 'medium' | 'heavy' = 'light'
): boolean => {
  if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
    try {
      if (style === 'heavy') {
        return navigator.vibrate([35, 10, 20]);
      }
      if (style === 'medium') {
        return navigator.vibrate(25);
      }
      return navigator.vibrate(15);
    } catch {
      return false;
    }
  }
  return false;
};

/**
 * Trigger notification patterns for payment success, warnings, or errors
 */
export const triggerNotificationHaptic = (
  type: 'success' | 'warning' | 'error'
): boolean => {
  if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
    try {
      if (type === 'success') {
        return navigator.vibrate([15, 60, 20]);
      }
      if (type === 'warning') {
        return navigator.vibrate([30, 80, 30]);
      }
      return navigator.vibrate([40, 60, 40, 60, 40]);
    } catch {
      return false;
    }
  }
  return false;
};

export const triggerSuccessHaptic = (): boolean => triggerNotificationHaptic('success');
export const triggerWarningHaptic = (): boolean => triggerNotificationHaptic('warning');
export const triggerErrorHaptic = (): boolean => triggerNotificationHaptic('error');

/**
 * Universal dispatcher matching the mobile app `triggerUiHaptic` contract
 */
export const triggerUiHaptic = (type: UiHapticType = 'selection'): boolean => {
  switch (type) {
    case 'selection':
      return triggerSelectionHaptic();
    case 'impactLight':
      return triggerImpactHaptic('light');
    case 'impactMedium':
      return triggerImpactHaptic('medium');
    case 'impactHeavy':
      return triggerImpactHaptic('heavy');
    case 'success':
    case 'notificationSuccess':
      return triggerNotificationHaptic('success');
    case 'warning':
    case 'notificationWarning':
      return triggerNotificationHaptic('warning');
    case 'error':
    case 'notificationError':
      return triggerNotificationHaptic('error');
    case 'none':
    default:
      return false;
  }
};

export default triggerUiHaptic;
